import { ref } from 'vue';
import api from '@/api';
import { withPdfPassword } from '@/utils/pdf/withPdfPassword';

export interface PickerFile {
  id: string;
  name: string;
  mimeType: string;
}

export interface ImportedDocument {
  id: string;
  name: string;
  documentName: string;
  documentKey: string;
  documentType: string;
  documentSize: number;
  url: string;
}

// Supported MIME types for the Picker filter
const PICKER_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
].join(',');

// Shared state across all instances
const isAvailable = ref(false);
const isConnected = ref(false);
const connectionEmail = ref('');
const connectionName = ref('');
const configLoaded = ref(false);
const storedApiKey = ref('');
const storedProjectNumber = ref('');

let pickerApiLoaded = false;

/** Dynamically load the Google API script and Picker library */
function loadPickerApi(): Promise<void> {
  if (pickerApiLoaded) return Promise.resolve();

  return new Promise((resolve, reject) => {
    // Check if gapi is already on the page
    if (window.gapi) {
      window.gapi.load('picker', {
        callback: () => { pickerApiLoaded = true; resolve(); },
        onerror: () => reject(new Error('Failed to load Google Picker')),
      });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.gapi.load('picker', {
        callback: () => { pickerApiLoaded = true; resolve(); },
        onerror: () => reject(new Error('Failed to load Google Picker')),
      });
    };
    script.onerror = () => reject(new Error('Failed to load Google API script'));
    document.head.appendChild(script);
  });
}

export function useGoogleDrive() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /** Load admin config and connection status */
  const loadConfig = async (force = false): Promise<void> => {
    if (configLoaded.value && !force) return;

    try {
      const [configRes, statusRes] = await Promise.all([
        api.get('/integrations/google-drive/config'),
        api.get('/integrations/google-drive/status'),
      ]);

      const configData = configRes.data.data;
      isAvailable.value = configData.enabled && !!configData.clientId;
      storedApiKey.value = configData.apiKey || '';
      storedProjectNumber.value = configData.projectNumber || '';

      const statusData = statusRes.data.data;
      isConnected.value = statusData.connected || false;
      connectionEmail.value = statusData.email || '';
      connectionName.value = statusData.name || '';

      configLoaded.value = true;
    } catch {
      isAvailable.value = false;
      isConnected.value = false;
      configLoaded.value = true;
    }
  };

  /** Reload just the connection status (after connect/disconnect) */
  const refreshStatus = async (): Promise<void> => {
    try {
      const statusRes = await api.get('/integrations/google-drive/status');
      const statusData = statusRes.data.data;
      isConnected.value = statusData.connected || false;
      connectionEmail.value = statusData.email || '';
      connectionName.value = statusData.name || '';
    } catch {
      isConnected.value = false;
    }
  };

  /** Redirect user to Google OAuth to connect their Drive */
  const connect = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await api.get('/integrations/google-drive/connect');
      const authUrl = response.data.data.authUrl;
      window.location.href = authUrl;
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to start Google Drive connection';
      isLoading.value = false;
    }
  };

  /** Disconnect Google Drive */
  const disconnect = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;
    try {
      await api.post('/integrations/google-drive/disconnect');
      isConnected.value = false;
      connectionEmail.value = '';
      connectionName.value = '';
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to disconnect Google Drive';
    } finally {
      isLoading.value = false;
    }
  };

  /** Get a fresh access token for the Picker */
  const getAccessToken = async (): Promise<string> => {
    const response = await api.get('/integrations/google-drive/access-token');
    return response.data.data.accessToken;
  };

  /** Open Google Picker and return selected files */
  const openPicker = async (): Promise<PickerFile[]> => {
    error.value = null;

    if (!storedApiKey.value) {
      error.value = 'Google Drive API Key is not configured. Contact your administrator.';
      return [];
    }

    try {
      // Load Picker API and get access token in parallel
      const [, accessToken] = await Promise.all([
        loadPickerApi(),
        getAccessToken(),
      ]);

      return new Promise<PickerFile[]>((resolve) => {
        const docsView = new google.picker.DocsView(google.picker.ViewId.DOCS)
          .setMimeTypes(PICKER_MIME_TYPES)
          .setIncludeFolders(true);

        const builder = new google.picker.PickerBuilder()
          .addView(docsView)
          .setOAuthToken(accessToken)
          .setDeveloperKey(storedApiKey.value)
          .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
          .setTitle('Select documents to import')
          .setCallback((data: google.picker.ResponseObject) => {
            if (data.action === google.picker.Action.PICKED) {
              const files: PickerFile[] = data.docs.map((doc: google.picker.DocumentObject) => ({
                id: doc.id,
                name: doc.name,
                mimeType: doc.mimeType,
              }));
              resolve(files);
            } else if (data.action === google.picker.Action.CANCEL) {
              resolve([]);
            }
          });

        // setAppId is required with drive.file scope to grant file access
        if (storedProjectNumber.value) {
          builder.setAppId(storedProjectNumber.value);
        }

        builder.build().setVisible(true);
      });
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || err.message || 'Failed to open Google Drive picker';
      return [];
    }
  };

  /** Import a file from Google Drive */
  const importFile = async (fileId: string, fileName: string, mimeType: string): Promise<ImportedDocument> => {
    isLoading.value = true;
    error.value = null;
    try {
      // Asks the user for a password if the Drive file is a protected PDF.
      const response = await withPdfPassword((pdfPassword) =>
        api.post('/integrations/google-drive/import', {
          fileId,
          fileName,
          mimeType,
          pdfPassword,
        }, { timeout: 120000 }) // 2 min timeout for large files + DOCX conversion
      );

      return response.data.data as ImportedDocument;
    } catch (err: any) {
      const message = err.response?.data?.error?.message || err.message || 'Failed to import file';
      error.value = message;
      throw new Error(message);
    } finally {
      isLoading.value = false;
    }
  };

  /** Reset shared state on logout so stale config doesn't persist across accounts */
  const resetState = () => {
    isAvailable.value = false;
    isConnected.value = false;
    connectionEmail.value = '';
    connectionName.value = '';
    configLoaded.value = false;
    storedApiKey.value = '';
    storedProjectNumber.value = '';
  };

  return {
    // State
    isAvailable,
    isConnected,
    isLoading,
    error,
    connectionEmail,
    connectionName,
    // Actions
    loadConfig,
    refreshStatus,
    connect,
    disconnect,
    openPicker,
    importFile,
    resetState,
  };
}
